import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function DriverPrivacy() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">

      {/* BACK BUTTON */}
      <Link
        to="/policies"
        className="text-orange-500 font-semibold flex items-center gap-2"
      >
        <ArrowLeft size={16} />
        Back to Policies
      </Link>

      {/* TITLE */}
      <h1 className="text-3xl font-bold mt-4 mb-2">
        Jopo Driver App - Privacy & Data Protection Policy
      </h1>

      <p className="text-gray-600 mb-6">
        Last Updated: 10/02/2026.
      </p>

      <p className="text-gray-700 mb-6">
        Jopo delivery “we” are committed to protecting the personal data of delivery partners
        (“drivers” or “riders”) who use the Jopo delivery Driver App. This policy explains how we
        collect, use, store, share, and protect your personal information in line with the Tanzania
        Personal Data Protection Act, 2022 and applicable regulations.
      </p>

      <ol className="list-decimal list-inside space-y-4 text-gray-700">

        {/* 1 */}
        <li>
          <strong>Information We Collect.</strong>

          <p className="mt-1">
            We collect only what is necessary to onboard drivers, operate the platform, and ensure
            service quality and compliance.
          </p>

          <ol type="a" className="list-inside ml-6 mt-2 space-y-1">

            <li>
              <strong >a)Personal & Identification Information.</strong>
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li>Full name</li>
                <li>Phone number and email address</li>
                <li>National ID / Driving license details (for verification)</li>
                <li>Profile photo.</li>
                <li>Letter for Government (Residential area).</li>
              </ul>
            </li>

            <li>
              <strong>b)Vehicle & Work-Related Information</strong>
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li>Vehicle type and registration details</li>
                <li>Insurance information (where applicable)</li>
                <li>Work availability and delivery preferences.</li>
                <li>Region of Operations.</li>
              </ul>
            </li>

            <li>
              <strong>c)Location & Activity Data</strong>
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li>Real-time GPS location while the app is active.</li>
                <li>Trip history, pickup and drop-off locations.</li>
                <li>Time stamps and delivery status.</li>
              </ul>
            </li>

            <li>
              <strong>d)Payment & Earnings Information</strong>
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li>Mobile money or bank payout details</li>
                <li>Earnings, commissions, and transaction records</li>
              </ul>
            </li>

            <li>
              <strong>e)Device & App Usage Data</strong>
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li>Device type, app version, crash logs, and basic analytics to improve performance and security.</li>
              </ul>
            </li>

          </ol>
        </li>

        {/* 2 */}
        <li>
          <strong>How We Use Your Information</strong>

          <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
            <li>Verify your identity and onboard you as a driver.</li>
            <li>Match you with delivery requests.</li>
            <li>Enable navigation, tracking, and delivery confirmation.</li>
            <li>Process earnings and payouts.</li>
            <li>Monitor service quality, safety, and fraud prevention.</li>
            <li>Communicate important updates, policy changes, and support messages.</li>
            <li>Comply with legal and regulatory obligations.</li>
          </ul>
        </li>

        {/* 3 */}
        <li>
          <strong>Legal Basis for Processing</strong>

          <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
            <li>Your consent during registration and use of the Driver App.</li>
            <li>Performance of a contract (to facilitate delivery work and payouts).</li>
            <li>Legal obligations (e.g., identity verification, compliance requirements).</li>
            <li>Legitimate interests related to platform safety, quality control, and fraud prevention.</li>
          </ul>
        </li>

        {/* 4 */}
        <li>
          <strong>Data Sharing & Third Parties</strong>

          <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
            <li>Customers: first name, phone number, and real-time location for delivery coordination.</li>
            <li>Payment partners: payout processing and transaction confirmation.</li>
            <li>Technology service providers: hosting, analytics, mapping, and support tools.</li>
            <li>Regulators or authorities: where required by Tanzanian law.</li>
          </ul>

          <p className="mt-1">
            We do not sell driver data to third parties.
          </p>
        </li>

        {/* 5 */}
        <li>
          <strong>Data Storage & Security</strong>
          <p className="mt-1">
            We take reasonable measures to protect your data, including secure data transmission,
            role-based access for authorized personnel only, and ongoing monitoring and security updates.
            Your personal data is retained only for as long as necessary to provide services or meet legal requirements.
          </p>
        </li>

        {/* 6 */}
        <li>
          <strong>Your Rights as a Driver</strong>

          <p className="mt-1">
            In line with Tanzanian data protection law, you have the right to:
          </p>

          <ol type="a" className="list-inside ml-6 mt-2 space-y-1">
            <li>Access your personal data.</li>
            <li>Request correction of inaccurate or outdated information.</li>
            <li>Request deletion of your data, where legally allowed.</li>
            <li>Withdraw consent for non-essential processing.</li>
            <li>Object to certain uses of your data.</li>
          </ol>

          <p className="mt-1">
            Requests can be submitted through support or the contact details below.
          </p>
        </li>

        {/* 7 */}
        <li>
          <strong>Data Retention</strong>
          <p className="mt-1">
            We retain driver data only for as long as needed to manage your account and delivery history,
            process payments, resolve disputes, and meet tax, regulatory, and audit obligations.
            Once no longer required, data is securely deleted or anonymized.
          </p>
        </li>

        {/* 8 */}
        <li>
          <strong>Background Checks & Verification</strong>
          <p className="mt-1">
            To maintain platform safety and trust, Jopo delivery may conduct verification checks using
            information you provide. This information is used strictly for onboarding and compliance purposes.
          </p>
        </li>

        {/* 9 */}
        <li>
          <strong>Cross-Border Data Processing</strong>
          <p className="mt-1">
            If any data is processed or stored outside Tanzania, Jopo delivery will ensure appropriate safeguards
            are in place to protect your information in accordance with applicable data protection standards.
          </p>
        </li>

        {/* 10 */}
        <li>
          <strong>Changes to This Policy</strong>
          <p className="mt-1">
            We may update this policy from time to time. Material changes will be communicated through the Driver App
            or official communication channels.
          </p>
        </li>

        {/* 11 */}
        <li>
          <strong>Contact Information</strong>

          <p className="mt-1">
            Data Protection Officer (DPO)<br />
            Jopo delivery<br />
            Email: Support@jopodelivery.com<br />
            Phone: +255689966875
          </p>
        </li>

      </ol>
    </div>
  );
}